import ShopSection from './components/ShopSection'
import { useState } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'

function App() {
  const [activeTab, setActiveTab] = useState('home')

  // Navigation items
  const navItems: Array<{id: string; label: string}> = []

  // Other destinations for cross-linking
  const destinations = [
    { name: 'HelloXinjiang', url: 'https://helloxinjiang.pages.dev', emoji: '🏔️', description: 'Xinjiang Beauty' },
    { name: 'HelloLasa', url: 'https://hellolasa.pages.dev', emoji: '🕌', description: 'Tibetan Culture' },
    { name: 'HelloXizang', url: 'https://helloxizang.pages.dev', emoji: '🗻', description: 'Tibet Highlands' },
    { name: 'Wudang Time Chamber', url: 'https://wudangtimechamber.com', emoji: '🧘', description: 'Sacred Mountain Retreat for Men' }
  ]

  // Main attractions for Yichang
  const attractions = [
    {
      id: 1,
      title: 'Three Gorges Dam',
      description: 'The world\'s largest hydroelectric power station, an engineering marvel on the Yangtze River.',
      image: '⚡',
      features: ['Engineering tour', 'Visitor center', 'Ship lift', 'Panoramic views']
    },
    {
      id: 2,
      title: 'Yangtze River Three Gorges',
      description: 'Spectacular gorges along the Yangtze River: Qutang, Wu, and Xiling Gorges.',
      image: '🏞️',
      features: ['River cruises', 'Scenic hikes', 'Cultural sites', 'Photography']
    },
    {
      id: 3,
      title: 'Qingjiang Gallery',
      description: 'A pristine river valley with crystal-clear waters and stunning karst landscapes.',
      image: '🛶',
      features: ['Boat tours', 'Local villages', 'Karst formations', 'Clear waters']
    },
    {
      id: 4,
      title: 'Qu Yuan Temple',
      description: 'Dedicated to the famous poet Qu Yuan, located on the banks of the Yangtze River.',
      image: '🏯',
      features: ['Cultural heritage', 'Poetry garden', 'River views', 'Historical exhibits']
    }
  ]

  // Travel tips
  const travelTips = [
    {
      title: 'Best Time to Visit',
      description: 'Spring (March-May) and Autumn (September-November) offer pleasant weather for sightseeing.',
      icon: '📅'
    },
    {
      title: 'River Cruises',
      description: 'Book Yangtze River cruises in advance, especially during peak tourist seasons.',
      icon: '🚢'
    },
    {
      title: 'Local Cuisine',
      description: 'Try Yichang specialties like river fish dishes, spicy tofu, and local citrus fruits.',
      icon: '🍲'
    },
    {
      title: 'Travel Planning',
      description: 'Combine Three Gorges Dam visit with Yangtze River cruise for a complete experience.',
      icon: '🗺️'
    }
  ]

  return (
    <>
      <SEO 
        title="Discover Yichang - Gateway to the Three Gorges"
        description="Your comprehensive guide to Yichang tourism, attractions, culture, and travel tips. Explore Three Gorges Dam, Yangtze River cruises, and more."
        keywords="Yichang, Three Gorges, travel, tourism, attractions, Yangtze River,宜昌,三峡,旅游,景点,长江"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Google-style Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg font-bold">YC</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">HelloYichang</h1>
                <p className="text-gray-600 text-sm">Gateway to the Three Gorges</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === item.id
                      ? 'bg-emerald-50 text-emerald-600 font-medium'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Contact Button */}
            
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 to-teal-100">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-emerald-500">📍</span>
              <span className="text-sm font-medium">Hubei Province, China</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Discover <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Yichang</span><br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Gateway to the Three Gorges</span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-10 max-w-2xl">
              Experience the engineering marvel of the Three Gorges Dam and the breathtaking beauty 
              of the Yangtze River gorges. Yichang offers a unique blend of natural wonders and 
              modern engineering achievements.
            </p>

            
          </div>
        </div>
      </section>

      <ShopSection />

      {/* Free Tickets Promotion */}
      <section className="py-12 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mb-6">
              <span className="text-white text-2xl">🎫</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Free <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Yichang Attraction Tickets</span>
            </h2>
            <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
              Get up to 5 attraction tickets per person by sending an email
            </p>
            <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
                <div className="text-3xl">📧</div>
                <div className="text-left">
                  <p className="text-gray-800 font-medium">Email to Get Tickets</p>
                  <a 
                    href="mailto:info@helloyichang.com" 
                    className="text-lg font-semibold text-emerald-600 hover:text-emerald-800 transition-colors duration-200"
                  >
                    info@helloyichang.com
                  </a>
                </div>
                <a 
                  href="mailto:info@helloyichang.com"
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-md"
                >
                  Get Tickets Now
                </a>
              </div>
            </div>
            <p className="text-gray-600 text-sm mt-6">
              Limited tickets available, first come first served. Valid for Yichang attractions only.
            </p>
          </div>
        </div>
      </section>

      {/* News Section */}
      <NewsSection 
        website="helloyichang"
        limit={10000}
        title="Yichang Travel News"
        themeColor="green"
      />

      {/* Attractions Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Must-See <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Attractions</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover the engineering marvels and natural wonders of Yichang
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {attractions.map((attraction) => (
              <div key={attraction.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="text-4xl mb-4">{attraction.image}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{attraction.title}</h3>
                  <p className="text-gray-600 mb-4">{attraction.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {attraction.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-gray-700">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Travel <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Tips</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Essential information for your Yichang journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-3xl mb-4">{tip.icon}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{tip.title}</h4>
                <p className="text-gray-600">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Touch</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              For travel inquiries and assistance with your Yichang journey
            </p>
            
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
              <div className="text-4xl mb-4">📧</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Email</h3>
              <p className="text-gray-700 text-lg mb-6">
                Reach out to us for travel information and assistance
              </p>
              
              <div className="inline-flex items-center justify-center space-x-3 bg-white rounded-xl px-6 py-3.5 shadow-sm">
                <span className="text-emerald-500 text-xl">✉️</span>
                <a 
                  href="mailto:info@helloyichang.com" 
                  className="text-xl font-semibold text-gray-900 hover:text-emerald-600 transition-colors duration-200"
                >
                  info@helloyichang.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-lg font-bold">YC</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">HelloYichang</h3>
                  <p className="text-gray-400 text-sm">Yichang Travel Guide</p>
                </div>
              </div>
              <p className="text-gray-400">
                Your comprehensive guide to discovering the engineering marvels and natural beauty of Yichang, China.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Contact</h4>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <span>📧</span>
                  <a href="mailto:info@helloyichang.com" className="hover:text-white transition-colors duration-200">
                    info@helloyichang.com
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  For travel information and assistance with your Yichang journey
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Explore More Destinations</h4>
              <ul className="space-y-3">
                {destinations.map((destination) => (
                  <li key={destination.name}>
                    <a 
                      href={destination.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      <span className="text-lg">{destination.emoji}</span>
                      <span>
                        <span className="font-medium">{destination.name}</span>
                        <span className="block text-sm text-gray-500">{destination.description}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 HelloYichang. All rights reserved. | Yichang Travel Information Portal</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}

export default App